// File: views/Landing/Testimonials.jsx
import React, { useState, useRef, useEffect } from 'react';
import { FaPlay, FaTimes, FaCheckCircle } from 'react-icons/fa';
import styles from '../../styles/Testimonials.module.css';

/* Video testimonials — pulled from /public/Testimonials/ */
const VIDEO_TESTIMONIALS = [
  { src: '/Testimonials/Video.MP4', rank: 'Top 0.01% Earner' },
  { src: '/Testimonials/BeautyPlus_multi_save.MP4', rank: 'Top 0.05% Earner' },
  { src: '/Testimonials/ba982191-acc4-49d6-947a-2901cefb2f7d.MP4', rank: 'Top 0.1% Earner' },
  { src: '/Testimonials/9ff1d322ca714e4e849b6cee32227a79.MP4', rank: 'Top 0.5% Earner' },
  { src: '/Testimonials/v12044gd0000d2fs1rfog65ktmdcm2d0.MP4', rank: 'Top 1% Earner' },
  { src: '/Testimonials/1c70f57e-9711-4301-bbb4-dcedd3917e7b.MP4', rank: 'Top 1% Earner' },
  { src: '/Testimonials/71d31343e9a6040d55a70a7c86cbd8853688eaa5.MP4', rank: 'Verified Client' },
  { src: '/Testimonials/IMG_7679.MP4', rank: 'Verified Client' },
  { src: '/Testimonials/96d9d4b30d70492e85a231ddc0a2481f.MP4', rank: 'Verified Client' },
  { src: '/Testimonials/1b1ce56db0024d3e8c3fc94f331892ac.MP4', rank: 'Verified Client' },
  {
    src: '/Testimonials/SnapInsta.to_AQMr-rnaTuAGiBoXNpbXkPJiIY9udRGcqO7zebotlCOOT2bvbCyoTWDvrLqpfpwN94igbvcQcNeD7lenyiAmg5u_BMLX-CKQ85nZPlA.MP4',
    rank: 'Verified Client',
  },
].map((v) => ({
  ...v,
  name: 'Verified Creator',
  handle: '@premium.client',
  verified: true,
}));

/*
 * Image testimonials — all five cards share the same uniform layout:
 *   header  → avatar (photo OR gradient initial) + name + verified ✓ + handle
 *   media   → hero image (creator portrait OR comment screenshot)
 *   stars   → 5-star rating
 *   quote   → testimonial text in a quote block
 *   metric  → optional results metric pill
 *   footer  → source badge ("Facebook · Verified Client Result", etc.)
 */
const IMAGE_TESTIMONIALS = [
  {
    name: 'Zoe Renea',
    handle: '@zoerenea',
    avatar: '/Zoe_Renea.jpg',
    image: '/Zoe_Renea_BG.PNG',
    verified: true,
  },
  {
    name: 'Jaaden Kyrelle',
    handle: '@jaadenkyrelle',
    avatar: '/Jaaden_Kyrelle.jpg',
    image: '/Jaaden_Kyrelle_BG.PNG',
    verified: true,
  },
  {
    name: 'Johnny Sins',
    handle: '@johnnysins',
    avatar: '/Johnny_Sins.jpg',
    image: '/Johnny_Sins_BG.PNG',
    verified: true,
  },
  {
    name: 'Madi Collins',
    handle: '@madicollinsxx',
    initial: 'M',
    avatarGradient: 'linear-gradient(135deg, #FF4D8D 0%, #FF7A59 100%)',
    image: '/Testimonials/File.png',
    verified: true,
  },
];

/* ── Auto-thumbnail capture for inline videos ── */
function useVideoThumb(videoRef) {
  const [poster, setPoster] = useState(null);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    let cancelled = false;
    const capture = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = el.videoWidth || 720;
        canvas.height = el.videoHeight || 1280;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(el, 0, 0, canvas.width, canvas.height);
        if (!cancelled) setPoster(canvas.toDataURL('image/jpeg', 0.72));
      } catch (_) {}
    };
    const onLoaded = () => {
      try {
        el.currentTime = Math.min(1.2, (el.duration || 2) / 3);
      } catch (_) {
        capture();
      }
    };
    const onSeeked = () => capture();
    el.addEventListener('loadeddata', onLoaded);
    el.addEventListener('seeked', onSeeked);
    return () => {
      cancelled = true;
      el.removeEventListener('loadeddata', onLoaded);
      el.removeEventListener('seeked', onSeeked);
    };
  }, [videoRef]);
  return poster;
}

function VideoCard({ video, onPlay }) {
  const ref = useRef(null);
  const poster = useVideoThumb(ref);
  return (
    <button
      type="button"
      className={styles.videoCard}
      onClick={() => onPlay(video)}
      aria-label="Play testimonial"
    >
      <video
        ref={ref}
        src={video.src}
        muted
        playsInline
        preload="metadata"
        crossOrigin="anonymous"
        className={styles.videoCardMedia}
        style={poster ? { display: 'none' } : undefined}
      />
      {poster && <img src={poster} alt="" className={styles.videoCardPoster} />}
      <div className={styles.videoCardOverlay} />
      <div className={styles.videoCardPlay}><FaPlay /></div>
      <div className={styles.videoCardBottom}>
        <div className={styles.videoCardName}>
          {video.name}
          {video.verified && <FaCheckCircle className={styles.verifiedBadgeSm} />}
        </div>
        <div className={styles.videoCardHandle}>{video.handle}</div>
        <div className={styles.videoCardRank}>{video.rank}</div>
      </div>
    </button>
  );
}

/* ── Avatar: photo OR gradient initial — both same circle dimensions ── */
function Avatar({ t }) {
  if (t.avatar) {
    return (
      <div className={styles.avatarRingPhoto}>
        <img src={t.avatar} alt={`${t.name} portrait`} className={styles.avatarPhoto} />
      </div>
    );
  }
  return (
    <div className={styles.avatarInitialRing} style={{ background: t.avatarGradient }}>
      <span className={styles.avatarInitial}>{t.initial}</span>
    </div>
  );
}

function ImageReviewCard({ t }) {
  return (
    <article className={styles.reviewCard}>
      <header className={styles.reviewHeader}>
        <Avatar t={t} />
        <div className={styles.reviewIdentity}>
          <div className={styles.reviewName}>
            <span>{t.name}</span>
            {t.verified && <FaCheckCircle className={styles.verifiedBadge} aria-label="Verified" />}
          </div>
          <div className={styles.reviewHandle}>
            {t.handle}
          </div>
        </div>
      </header>

      <div className={styles.reviewMediaWrap}>
        <img
          className={styles.reviewMedia}
          src={t.image}
          alt={`Testimonial from ${t.name}`}
          loading="lazy"
        />
        <div className={styles.reviewMediaBadge}>
          <FaCheckCircle /> Verified
        </div>
      </div>
    </article>
  );
}

function VideoModal({ video, onClose }) {
  useEffect(() => {
    const handler = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
  if (!video) return null;
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalInner} onClick={(e) => e.stopPropagation()}>
        <button className={styles.modalClose} onClick={onClose} aria-label="Close video">
          <FaTimes />
        </button>
        <video className={styles.modalVideo} src={video.src} autoPlay controls playsInline />
        <div className={styles.modalCaption}>
          <div className={styles.modalCaptionName}>
            {video.name}
            {video.verified && <FaCheckCircle className={styles.verifiedBadgeSm} />}
          </div>
          <div className={styles.modalCaptionHandle}>
            {video.handle} &middot; {video.rank}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Testimonials() {
  const [activeVideo, setActiveVideo] = useState(null);
  const scrollerRef = useRef(null);
  const showImageTestimonials = true;

  const scrollByCards = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector(`.${styles.reviewCard}`);
    const step = card ? card.getBoundingClientRect().width + 24 : 360;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <section
      id="testimonials"
      className={`${styles.section} ${styles.testimonialsSection}`}
      data-anim
    >
      {/* Animated background */}
      <div className={styles.sectionBgRoot} aria-hidden="true">
        <div className={`${styles.sectionOrb} ${styles.sectionOrbPink}`} />
        <div className={`${styles.sectionOrb} ${styles.sectionOrbSky}`} />
        <div className={styles.sectionAurora} />
      </div>

      <div className={styles.inner}>
        <div className={styles.headerCompact}>
          <div className={styles.eyebrowSmall}>&#9733; Real Creator Results</div>
          <h2 className={styles.subTitle}>
            What our <span className={styles.titleAccent}>creators</span> are saying
          </h2>
        </div>

        <div className={styles.videoGrid}>
          {VIDEO_TESTIMONIALS.map((v) => (
            <VideoCard key={v.src} video={v} onPlay={setActiveVideo} />
          ))}
        </div>

        {showImageTestimonials && (
          <>
            <div className={styles.divider}>
              <span>Verified screenshots &amp; messages</span>
            </div>

            <div className={styles.reviewScrollerWrap}>
              <button
                className={`${styles.scrollArrow} ${styles.scrollArrowLeft}`}
                onClick={() => scrollByCards(-1)}
                aria-label="Scroll testimonials left"
              >
                ‹
              </button>
              <div className={styles.scrollFadeLeft} aria-hidden="true" />
              <div className={styles.reviewScroller} ref={scrollerRef}>
                {IMAGE_TESTIMONIALS.map((t) => (
                  <ImageReviewCard key={t.handle} t={t} />
                ))}
              </div>
              <div className={styles.scrollFadeRight} aria-hidden="true" />
              <button
                className={`${styles.scrollArrow} ${styles.scrollArrowRight}`}
                onClick={() => scrollByCards(1)}
                aria-label="Scroll testimonials right"
              >
                ›
              </button>
            </div>
          </>
        )}

        <div className={styles.trustStrip}>
          <div className={styles.trustItem}>
            <div className={styles.trustValue}>250+</div>
            <div className={styles.trustLabel}>Active Creators</div>
          </div>
          <div className={styles.trustItem}>
            <div className={styles.trustValue}>4.9&#9733;</div>
            <div className={styles.trustLabel}>Avg. Client Rating</div>
          </div>
          <div className={styles.trustItem}>
            <div className={styles.trustValue}>$30M+</div>
            <div className={styles.trustLabel}>Revenue Generated</div>
          </div>
          <div className={styles.trustItem}>
            <div className={styles.trustValue}>24/7</div>
            <div className={styles.trustLabel}>Dedicated Support</div>
          </div>
        </div>
      </div>

      <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />
    </section>
  );
}
